import { useState } from "react";
import { Sparkles, AlertCircle, ImagePlus } from "lucide-react";
import { runSetup, setToken } from "../lib/api";
import { suggestPalette } from "../lib/brand";

const readAsDataUrl = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = rej;
  r.readAsDataURL(file);
});

export default function Setup({ onDone }) {
  const [company, setCompany] = useState("");
  const [website, setWebsite] = useState("");
  const [primary, setPrimary] = useState("#1e3a8a");
  const [accent, setAccent] = useState("#f97316");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [logo, setLogo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const pickLogo = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { setError("Logo must be under 2 MB"); return; }
    setError("");
    const url = await readAsDataUrl(file);
    setLogo({ url });
    const [first, second] = await suggestPalette(url);  // start from the logo's own colours; both can still be changed
    if (first) setPrimary(first);
    if (second) setAccent(second);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError("Passwords don't match"); return; }
    setBusy(true); setError("");
    try {
      const { token } = await runSetup({
        company_name: company, user_name: name, password, email: email.trim(),
        website: website.trim(), primary_color: primary, accent_color: accent,
        logo_base64: logo ? logo.url : null, keys: {},
      });
      setToken(token);
      onDone();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Setup failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="login-shell setup-shell">
      <form className="modal login-card setup-card" onSubmit={submit} data-testid="setup-form">
        <div className="modal-icon"><Sparkles size={20} /></div>
        <h2>Welcome — let's set up your CRM</h2>
        <p>Everything you enter stays on this computer. You can change any of it later in Settings.</p>

        <h3>Your company</h3>
        <label>Company name
          <input value={company} onChange={(e) => setCompany(e.target.value)} required autoFocus data-testid="setup-company" />
        </label>
        <label>Website (optional)
          <input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="example.com" data-testid="setup-website" />
        </label>
        <div className="two-col">
          <label>Main colour<input type="color" value={primary} onChange={(e) => setPrimary(e.target.value)} data-testid="setup-primary" /></label>
          <label>Accent colour<input type="color" value={accent} onChange={(e) => setAccent(e.target.value)} data-testid="setup-accent" /></label>
        </div>
        <label>Logo (PNG, JPG or SVG, optional)
          <span className="logo-pick">
            {logo ? <img src={logo.url} alt="Logo preview" /> : <ImagePlus size={20} />}
            <input type="file" accept="image/png,image/jpeg,image/svg+xml" onChange={(e) => pickLogo(e.target.files[0])} data-testid="setup-logo" />
          </span>
        </label>

        <h3>You</h3>
        <label>Your name
          <input value={name} onChange={(e) => setName(e.target.value)} required data-testid="setup-name" />
        </label>
        <label>Your email (you'll sign in with it; teammates get their own logins later)
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="setup-email" />
        </label>
        <label>Password (at least 6 characters)
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="setup-password" />
        </label>
        <label>Confirm password
          <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="setup-confirm" />
        </label>

        <p className="muted">Next, a short guide helps you connect email, lead search, calling and AI. Every step can be skipped.</p>

        {error && <div className="error-strip" data-testid="setup-error"><AlertCircle size={14} /> {error}</div>}
        <button className="primary-button full" type="submit" disabled={busy} data-testid="setup-submit">
          {busy ? "Setting up…" : "Finish setup"}
        </button>
      </form>
    </div>
  );
}
